import type { Employee, RequestField, Shift, TimeOff } from "../lib/types";
import { TIME_OFF_LABEL } from "../lib/types";
import { readableTextOn } from "../lib/colors";
import { useBetaDesign } from "../lib/platform";
import { Button } from "./ui/Button";
import { formatRequestFieldValue } from "./RequestFields";

// Case-insensitive search across the fields a supervisor might
// actually look for: the officer's name / badge / rank, the free
// form note, and every value they typed into metadata fields. An
// empty query matches everything so callers can skip the guard.
export function requestMatchesSearch(
  t: TimeOff,
  query: string,
  empById: Record<string, Employee | undefined>,
): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  const emp = empById[t.employeeId];
  const parts: Array<string | null | undefined> = [
    emp?.firstName, emp?.lastName, emp?.badgeNumber, emp?.rank,
    t.note,
  ];
  for (const v of Object.values(t.metadata ?? {})) {
    if (v == null || v === false) continue;
    parts.push(typeof v === "boolean" ? "" : String(v));
  }
  return parts.filter(Boolean).join(" ").toLowerCase().includes(q);
}

// Grouped table of requests (Active / Completed) shared by the
// Training and Time-off pages. Columns are fixed aside from the
// Type column, which only makes sense on Time-off where the list
// mixes vacation / sick / court / etc. Training already filters
// to a single type upstream.
//
// When `groupByShift` is true, rows are bucketed by each officer's
// home shift and rendered under small section headers so supervisors
// can see the request pile shift-by-shift. When false (or when a
// specific shift is already filtered upstream), the rows render
// flat in the order the caller passed them.
//
// Each row's action column is a single button — Open for pending
// requests, Export for completed — that fires the `onView`
// callback. The page opens a detail modal where the actual
// approve / deny / print decisions happen, so the list stays a
// pure scan-and-pick surface.
export function RequestSection({
  title, count, emptyMessage, rows, empById, fields,
  showType, onView,
  shiftById, groupByShift,
}: {
  title: string;
  count: number;
  emptyMessage: string;
  rows: TimeOff[];
  empById: Record<string, Employee>;
  fields: RequestField[];
  showType?: boolean;
  onView: (t: TimeOff) => void;
  shiftById?: Record<string, Shift>;
  groupByShift?: boolean;
}) {
  // Visible columns: Employee + Range + (Type?) + Status + (action?).
  // Beta drops the trailing Open/Export action column (the row is
  // clickable, so the button is redundant), so colSpan for empty-
  // state and group-header rows shifts down by one in that mode.
  const betaDesign = useBetaDesign();
  const columnCount = 3 + (showType ? 1 : 0) + (betaDesign ? 0 : 1);
  const groups = groupByShift
    ? groupRowsByShift(rows, empById, shiftById ?? {})
    : null;
  // "Unassigned" fall-through group has no matching shift color —
  // slate-500 matches the Schedule page's fallback for the same case.
  const UNASSIGNED_COLOR = "#64748b";

  return (
    <section>
      <h2 className="mb-2 text-sm font-semibold text-slate-700">
        {title}
        <span className="ml-2 font-normal text-slate-400">({count})</span>
      </h2>
      <div
        className={
          "bg-white border border-slate-200 rounded-lg "
          + (betaDesign ? "" : "overflow-x-auto")
        }
      >
        <table className={"w-full " + (betaDesign ? "text-xs" : "min-w-[640px] text-sm")}>
          <thead className="bg-slate-50 text-slate-600 text-left">
            <tr>
              <th className={(betaDesign ? "px-2" : "px-3") + " py-2 font-medium"}>Employee</th>
              <th className={(betaDesign ? "px-2" : "px-3") + " py-2 font-medium"}>Range</th>
              {showType && <th className={(betaDesign ? "px-2" : "px-3") + " py-2 font-medium"}>Type</th>}
              <th className={(betaDesign ? "px-2" : "px-3") + " py-2 font-medium"}>Status</th>
              {!betaDesign && <th className="px-3 py-2 print:hidden"></th>}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {groups
              ? groups.map(({ label, color, rows: shiftRows }) => (
                  <ShiftGroup
                    key={label}
                    label={label}
                    color={color ?? UNASSIGNED_COLOR}
                    rows={shiftRows}
                    columnCount={columnCount}
                    empById={empById}
                    fields={fields}
                    showType={showType}
                    onView={onView}
                  />
                ))
              : rows.map((t) => (
                  <RequestRow
                    key={t.id}
                    t={t}
                    empById={empById}
                    fields={fields}
                    showType={showType}
                    onView={onView}
                  />
                ))}
          </tbody>
        </table>
        {rows.length === 0 && (
          <div className="p-6 text-center text-sm text-slate-500">
            {emptyMessage}
          </div>
        )}
      </div>
    </section>
  );
}

// Orders rows into per-shift buckets, carrying the shift's name,
// color, and sortOrder so group headers can echo the Schedule page's
// palette. Officers without a home shift fall through into a
// trailing "Unassigned" bucket with no color.
type ShiftBucket = {
  label: string;
  color: string | null;
  sort: number;
  rows: TimeOff[];
};
function groupRowsByShift(
  rows: TimeOff[],
  empById: Record<string, Employee>,
  shiftById: Record<string, Shift>,
): ShiftBucket[] {
  const buckets = new Map<string, ShiftBucket>();
  for (const t of rows) {
    const emp = empById[t.employeeId];
    const shift = emp?.homeShiftId ? shiftById[emp.homeShiftId] : undefined;
    const key = shift?.id ?? "__unassigned__";
    const bucket = buckets.get(key) ?? {
      label: shift?.name ?? "Unassigned",
      color: shift?.color ?? null,
      sort: shift?.sortOrder ?? Number.POSITIVE_INFINITY,
      rows: [],
    };
    bucket.rows.push(t);
    buckets.set(key, bucket);
  }
  return Array.from(buckets.values()).sort(
    (a, b) => a.sort - b.sort || a.label.localeCompare(b.label),
  );
}

function ShiftGroup({
  label, color, rows, columnCount, empById, fields, showType, onView,
}: {
  label: string;
  color: string;
  rows: TimeOff[];
  columnCount: number;
  empById: Record<string, Employee>;
  fields: RequestField[];
  showType?: boolean;
  onView: (t: TimeOff) => void;
}) {
  // Same neon-bar treatment as the Schedule's section header: the
  // saturated shift color reads as the background, with text colour
  // flipped via luminance so yellow / lime keep black text and the
  // darker hues switch to white. The count chip uses the same
  // colour at reduced opacity so it reads as secondary metadata
  // on both light and dark backgrounds.
  const fg = readableTextOn(color);
  const metaFg = fg === "#ffffff" ? "rgba(255,255,255,0.78)" : "rgba(15,23,42,0.65)";
  return (
    <>
      <tr>
        <td
          colSpan={columnCount}
          className="themed-fill px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wider"
          style={{ color: fg, backgroundColor: color, ["--themed-color" as string]: color }}
        >
          <span className="inline-block w-3 text-center">●</span>
          <span className="ml-2">{label}</span>
          <span className="ml-2 font-normal normal-case tracking-normal opacity-80 dark:opacity-70"
                style={{ color: metaFg }}>
            ({rows.length})
          </span>
        </td>
      </tr>
      {rows.map((t) => (
        <RequestRow
          key={t.id}
          t={t}
          empById={empById}
          fields={fields}
          showType={showType}
          onView={onView}
        />
      ))}
    </>
  );
}

function RequestRow({
  t, empById, fields, showType, onView,
}: {
  t: TimeOff;
  empById: Record<string, Employee>;
  fields: RequestField[];
  showType?: boolean;
  onView: (t: TimeOff) => void;
}) {
  const e = empById[t.employeeId];
  const fieldEntries = fields
    .map((f) => ({ f, v: t.metadata?.[f.key] }))
    .filter(({ v, f }) => {
      if (f.fieldType === "checkbox") return v === true;
      return v !== undefined && v !== null && v !== "";
    });
  const isPending = t.status === "requested";
  // Beta tightens padding and drops the trailing Open/Export
  // button column — the whole row is already clickable on iOS so
  // a separate affordance just eats horizontal space.
  const betaDesign = useBetaDesign();
  const pad = betaDesign ? "px-2 py-2" : "px-3 py-2";
  return (
    <tr
      className="hover:bg-slate-50 align-top cursor-pointer"
      onClick={() => onView(t)}
    >
      <td className={`${pad} font-medium text-slate-900`}>
        {e ? `${e.lastName}, ${e.firstName}` : t.employeeId}
      </td>
      <td className={pad}>
        <div>{t.startDate} → {t.endDate}</div>
        {fieldEntries.length > 0 && (
          <dl className="mt-1 grid grid-cols-1 md:grid-cols-2 gap-x-4 text-xs text-slate-600">
            {fieldEntries.map(({ f, v }) => (
              <div key={f.id} className="flex gap-1">
                <dt className="font-medium text-slate-500">{f.label}:</dt>
                <dd className="text-slate-700 font-mono">
                  {formatRequestFieldValue(f, v)}
                </dd>
              </div>
            ))}
          </dl>
        )}
        {t.note && (
          <div className="mt-1 text-xs italic text-slate-500">
            {t.note}
          </div>
        )}
      </td>
      {showType && (
        <td className={pad}>{TIME_OFF_LABEL[t.type]}</td>
      )}
      <td className={`${pad} font-medium ${
        t.status === "approved"    ? "text-green-700" :
        t.status === "denied"      ? "text-red-700" :
        t.status === "l2_approved" ? "text-sky-700" :
                                     "text-amber-700"
      }`}>
        {t.status === "l2_approved" ? "Pending L1/Owner" : t.status}
      </td>
      {!betaDesign && (
        <td className="px-3 py-2 text-right print:hidden">
          <Button
            size="sm"
            variant={isPending ? "primary" : "secondary"}
            onClick={(ev) => { ev.stopPropagation(); onView(t); }}
          >
            {isPending ? "Open" : "Export"}
          </Button>
        </td>
      )}
    </tr>
  );
}
